import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { t } from "i18next";

import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { AdditionalInfoBadge } from "@/src/components/atoms/AdditionalInfoBadge";
import CarouselArrow from "@/src/components/atoms/CarouselArrow";
import { TargetPriceOverlay } from "@/src/components/atoms/TargetPriceOverlay";
import { ZoomOverlay } from "@/src/components/atoms/ZoomOverlay";
import CarouselThumbnails from "@/src/components/molecules/CarouselThumbnails";

import { UnifiedCarouselImage } from "@/src/types/carousel";

interface UnifiedImageCarouselViewProps {
  images: UnifiedCarouselImage[];
  currentIndex: number;
  onNext: () => void;
  onPrevious: () => void;
  onImageClick?: () => void;
  arrowVisibility?: "always" | "never" | "onHover";
  leftArrowIcon?: ReactNode;
  rightArrowIcon?: ReactNode;
  showCounter?: boolean;
  counterPosition?: "top" | "bottom";
  showThumbnails?: boolean;
  onThumbnailClick?: (index: number) => void;
  renderImage: (image: UnifiedCarouselImage, index: number) => ReactNode;
  sx?: SxProps;
  containerSx?: SxProps;
  maxWidth?: string | number;
  height?: string | number;
  showAdditionalInfoBadge?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const UnifiedImageCarouselView = (props: UnifiedImageCarouselViewProps) => {
  const {
    images,
    currentIndex,
    onNext,
    onPrevious,
    onImageClick,
    arrowVisibility = "always",
    leftArrowIcon,
    rightArrowIcon,
    showCounter = false,
    counterPosition = "bottom",
    showThumbnails = false,
    onThumbnailClick,
    renderImage,
    sx,
    containerSx,
    maxWidth,
    height,
    showAdditionalInfoBadge = false,
    onMouseEnter,
    onMouseLeave
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isHovered, setIsHovered] = useState(false);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  const totalImages = images.length;
  const showNavigation = totalImages > 1;
  const showArrows = showNavigation && arrowVisibility !== "never";
  const shouldShowArrows =
    arrowVisibility === "always" ||
    (arrowVisibility === "onHover" && isHovered);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onMouseLeave?.();
  };

  const handleImageKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!onImageClick) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onImageClick();
      }
    },
    [onImageClick]
  );

  const isEditableElement = (element: EventTarget | null): boolean => {
    if (!element || !(element instanceof HTMLElement)) {
      return false;
    }

    const tagName = element.tagName.toLowerCase();
    if (tagName === "input" || tagName === "textarea" || tagName === "select") {
      return true;
    }

    if (element.contentEditable === "true") {
      return true;
    }

    if (element.closest('input, textarea, select, [contenteditable="true"]')) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      const container = carouselContainerRef.current;
      if (!container || !showNavigation) {
        return;
      }

      const isContainerFocused =
        container === document.activeElement ||
        container.contains(document.activeElement);

      const targetIsEditable = isEditableElement(event.target);

      if (targetIsEditable && !isContainerFocused) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onPrevious();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onNext();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => {
      globalThis.removeEventListener("keydown", handleKeyDown);
    };
  }, [onNext, onPrevious, showNavigation]);

  if (totalImages === 0) {
    return null;
  }

  const currentImage = images[currentIndex];

  const getAriaLabel = () => {
    if (totalImages > 1) {
      return t("consumer.wizard.carousel.openLightboxWithIndex", {
        index: currentIndex + 1,
        total: totalImages
      });
    }
    return t("consumer.wizard.carousel.openLightbox");
  };

  return (
    <Box
      ref={carouselContainerRef}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: maxWidth ?? "100%",
        margin: "0 auto",
        ...containerSx
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          ...(height
            ? { height }
            : {
                // Use aspect-ratio to maintain consistent container size
                // This ensures the container stays the same size regardless of image orientation
                aspectRatio: "16 / 9",
                minHeight: "200px"
              }),
          cursor: onImageClick ? "pointer" : "default",
          WebkitTapHighlightColor: "transparent",
          overflow: "hidden",
          ...sx
        }}
        onClick={onImageClick}
        onKeyDown={handleImageKeyDown}
        role={onImageClick ? "button" : undefined}
        tabIndex={onImageClick ? 0 : undefined}
        aria-label={onImageClick ? getAriaLabel() : undefined}
      >
        {currentImage && renderImage(currentImage, currentIndex)}

        {currentImage?.targetPrice && (
          <TargetPriceOverlay
            targetPrice={currentImage.targetPrice}
            isMobile={isMobile}
          />
        )}

        {showAdditionalInfoBadge && currentImage?.hasAdditionalInfo && (
          <AdditionalInfoBadge
            createdAt={currentImage.createdAt}
            isMobile={isMobile}
            position="bottom-right"
          />
        )}

        {onImageClick && (
          <ZoomOverlay isMobile={isMobile} onImageClick={onImageClick} />
        )}

        {showArrows && (
          <>
            <CarouselArrow
              direction="left"
              onClick={onPrevious}
              icon={leftArrowIcon}
              isVisible={shouldShowArrows}
            />
            <CarouselArrow
              direction="right"
              onClick={onNext}
              icon={rightArrowIcon}
              isVisible={shouldShowArrows}
            />
          </>
        )}

        {showCounter && showNavigation && (
          <Box
            sx={{
              position: "absolute",
              ...(counterPosition === "top" ? { top: 0 } : { bottom: 0 }),
              left: 0,
              color: theme.palette.common.white,
              fontSize: isMobile
                ? theme.typography.body2.fontSize
                : theme.typography.body1.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              zIndex: 25,
              backgroundColor: theme.palette.misc.backdropOverlay,
              padding: isMobile ? "4px 8px" : "6px 16px",
              borderRadius: "4px",
              minWidth: isMobile ? "50px" : "70px",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none"
            }}
          >
            {currentIndex + 1}/{totalImages}
          </Box>
        )}
      </Box>

      {showThumbnails && !isMobile && (
        <CarouselThumbnails
          images={images}
          currentIndex={currentIndex}
          onThumbnailClick={onThumbnailClick}
          showNavigation={showNavigation}
        />
      )}
    </Box>
  );
};

export default UnifiedImageCarouselView;
